import { useMemo } from "react";
import styled from "@emotion/styled";
import { GrCheckbox, GrRadio, palette } from "@jnpmedi/grebob";
import { defaultMarkerColor, getColorBySignerIndex } from "@newComponents/common/Viewer/types";
import { DocumentMarker, useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { useEConsentContext } from "@newHooks/context/eConsent/eConsent.context";
import { fadeInBorderMarker } from "@assets/styles/animations";
import { EConsentMarkerProps } from "./EConsentMarker";
import { DocumentSignerAttrType, DocumentSignerResponsiveItemTypeValue, DocumentSignType } from "@maven-rest/docs/DocumentSign.dto";
import { v4 as uuidv4 } from "uuid";

export function CheckBoxMarker(props: EConsentMarkerProps) {
  const { markers, setMarkers } = useDocumentMarkerContext();
  const { signee } = useEConsentContext();

  const responsiveItemTypes = getResponsiveItemTypes(props.marker.attr);

  const groupUuid = signee.attrs.find(
    (data) => data.attr === responsiveItemTypes.fieldIndex && data.attrUuid === props.marker.groupUuid
  )?.groupUuid;

  // NOTE: ICF 생성시, 단일 선택과 질문 항목을 구분하는 값
  const typeValue = signee.attrs.find((data) => data.groupUuid === groupUuid && data.attr === responsiveItemTypes.type)?.value;

  // NOTE: agentAvailableMark일 경우 value type 없이 attr value에 boolean 저장
  const isChecked = useMemo(
    () =>
      !!markers.find(
        (e) => e.groupUuid === props.marker.groupUuid && e.attr === responsiveItemTypes.value && e.value === props.marker.attrUuid
      ) || props.marker.value === "true",
    [markers, props.marker, responsiveItemTypes.value]
  );

  const { isFulfilled, isSingleResponsiveItemType } = useMemo(() => {
    // NOTE: 해당 마커의 옵션
    const fieldIndex = markers.find((e) => e.attrUuid === props.marker.groupUuid && e.attr === responsiveItemTypes.fieldIndex);

    // NOTE: 필수 응답 수
    let requiredCount = markers.find((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.fieldRequired)?.value;

    const isSingleResponsiveItemType =
      markers.find((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.type)?.value ===
      DocumentSignerResponsiveItemTypeValue.Single;

    // NOTE: DocumentSignerResponsiveItemTypeValue은 Single은 필수 응답 수 1로 설정
    if (isSingleResponsiveItemType) {
      requiredCount = "1";
    }

    if (!requiredCount) {
      const missing = markers.find((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.missing)?.value;
      const wrong = markers.find((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.wrong)?.value;

      if (missing || wrong) {
        requiredCount = "1";
      } else {
        return { isFulfilled: true, isSingleResponsiveItemType };
      }
    }

    // NOTE: 같은 그룹의 옵션
    const fieldIndexes = markers.filter((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.fieldIndex);

    const responseValues = markers.filter(
      (e) => e.attr === responsiveItemTypes.value && fieldIndexes.find((x) => x.attrUuid === e.groupUuid)
    );

    return {
      isFulfilled: responseValues.length >= Number(requiredCount),
      isSingleResponsiveItemType,
    };
  }, [markers, props.marker.groupUuid, responsiveItemTypes]);

  const altText = useMemo(() => {
    const markerGroupUuid = props.marker.groupUuid;
    const fieldAltText = markers.find((e) => e.groupUuid === markerGroupUuid && e.attr === responsiveItemTypes.fieldAltText)?.value;

    return fieldAltText;
  }, [markers, props.marker.groupUuid, responsiveItemTypes.fieldAltText]);

  const updateMarkerValues = (attrType: DocumentSignerAttrType, marker: DocumentMarker) => {
    return markers.filter((e) => e.attr === attrType).map((el) => ({ ...el, value: marker.value === "true" ? "" : el.value }));
  };

  const handleClickMarker = (e: React.MouseEvent<HTMLDivElement>, marker: DocumentMarker) => {
    if (props.isModeSigning) {
      if (marker.attr === DocumentSignerAttrType.AgentAvailableMark) {
        const newAgentInputItemValues = updateMarkerValues(DocumentSignerAttrType.AgentInputItemValue, marker);
        const newAgentDropdownValues = updateMarkerValues(DocumentSignerAttrType.AgentDropdownItemValue, marker);
        const newAgentDateValues = updateMarkerValues(DocumentSignerAttrType.AgentDateItemValue, marker);
        const newAgentAddressValues = updateMarkerValues(DocumentSignerAttrType.AgentAddressItemValue, marker);
        const newAgentFollowWritingValues = updateMarkerValues(DocumentSignerAttrType.AgentFollowWritingItemValue, marker);

        const remainMarkers = markers.filter(
          (e) =>
            ![
              DocumentSignerAttrType.AgentAvailableMark,
              DocumentSignerAttrType.AgentInputItemValue,
              DocumentSignerAttrType.AgentDropdownItemValue,
              DocumentSignerAttrType.AgentDateItemValue,
              DocumentSignerAttrType.AgentAddressItemValue,
              DocumentSignerAttrType.AgentResponsiveItemValue,
              DocumentSignerAttrType.AgentFollowWritingItemValue,
            ].includes(e.attr)
        );

        const updatedMarker = {
          ...marker,
          value: marker.value === "true" ? "false" : "true",
        };

        setMarkers([
          ...remainMarkers,
          updatedMarker,
          ...newAgentInputItemValues,
          ...newAgentDropdownValues,
          ...newAgentDateValues,
          ...newAgentAddressValues,
          ...newAgentFollowWritingValues,
        ]);
      }

      if ([DocumentSignerAttrType.ResponsiveItemFieldMark, DocumentSignerAttrType.AgentResponsiveItemFieldMark].includes(marker.attr)) {
        const fieldIndex = markers.find((e) => e.attrUuid === marker.groupUuid && e.attr === responsiveItemTypes.fieldIndex);

        const responsiveItemFieldIndexes = markers.filter(
          (e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.fieldIndex
        );

        const responseValues = markers.filter(
          (e) => e.attr === responsiveItemTypes.value && responsiveItemFieldIndexes.find((x) => x.attrUuid === e.groupUuid)
        );

        let valueCount = responseValues.length || 0;

        const answersCount = responsiveItemFieldIndexes.filter((e) =>
          markers.find((m) => m.groupUuid === e.attrUuid && m.attr === responsiveItemTypes.fieldRequired)
        ).length;

        let requiredCount = Number(
          markers.find((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.requiredCount)?.value || 0
        );

        // NOTE: DocumentSignerResponsiveItemTypeValue은 Single은 필수 응답 수 1로 설정
        if (
          markers.find((e) => e.groupUuid === fieldIndex?.groupUuid && e.attr === responsiveItemTypes.type)?.value ===
          DocumentSignerResponsiveItemTypeValue.Single
        ) {
          requiredCount = 1;
        }

        const exist = !!markers.find((x) => x.groupUuid === marker.groupUuid && x.attr === responsiveItemTypes.value);

        setMarkers(
          markers
            .filter((x) => !(x.groupUuid === marker.groupUuid && x.attr === responsiveItemTypes.value))
            // NOTE: EConsent가 아닌 경우 fieldRequired가 1인 경우 라디오로 동작
            // TODO: 해당 조건이 필요한지 확인 필요 (E-Sign Advanced mode)
            .filter(
              (x) =>
                !(
                  props.signType !== DocumentSignType.EConsent &&
                  answersCount === 1 &&
                  markers.find((e) => e.attrUuid === x.groupUuid && e.attr === responsiveItemTypes.fieldIndex)?.groupUuid ===
                    fieldIndex?.groupUuid &&
                  x.attr === responsiveItemTypes.value
                )
            )
            // NOTE: 필수 응답 수가 넘어가면 기존 체크 값 해제
            .filter(
              (x) =>
                !(
                  requiredCount > 0 &&
                  valueCount + 1 > requiredCount &&
                  x.attr === responsiveItemTypes.value &&
                  markers.find((e) => e.attrUuid === x.groupUuid && responsiveItemTypes.fieldIndex)?.groupUuid === fieldIndex?.groupUuid
                )
            )
            .concat(
              exist
                ? []
                : [
                    {
                      id: marker.id,
                      signerUuid: marker.signerUuid,
                      target: marker.target,
                      signerIndex: marker.signerIndex,
                      attr: responsiveItemTypes.value!,
                      attrUuid: uuidv4(),
                      groupUuid: marker.groupUuid,
                      value: marker.attrUuid,
                      page: marker.page,
                      x: marker.x,
                      y: marker.y,
                    },
                  ]
            )
        );
      }
    }

    props.onClick?.(e);
  };

  return (
    <>
      <StyledCheckbox
        {...props}
        isChecked={isChecked}
        isFulfilled={isFulfilled}
        altText={altText}
        onClick={(e) => handleClickMarker(e, props.marker)}
        type={
          typeValue === DocumentSignerResponsiveItemTypeValue.Single || isSingleResponsiveItemType
            ? DocumentSignerResponsiveItemTypeValue.Single
            : DocumentSignerResponsiveItemTypeValue.Multiple
        }
      >
        {props.isModeSigning && (
          <>{isSingleResponsiveItemType ? <GrRadio checked={isChecked} size="large" /> : <GrCheckbox checked={isChecked} size="large" />}</>
        )}
      </StyledCheckbox>
    </>
  );
}

const getResponsiveItemTypes = (markerAttr: DocumentSignerAttrType) => {
  switch (markerAttr) {
    case DocumentSignerAttrType.ResponsiveItemFieldMark:
      return {
        index: DocumentSignerAttrType.ResponsiveItemIndex,
        type: DocumentSignerAttrType.ResponsiveItemType,
        missing: DocumentSignerAttrType.ResponsiveItemMissing,
        wrong: DocumentSignerAttrType.ResponsiveItemWrong,
        requiredCount: DocumentSignerAttrType.ResponsiveItemRequiredCount,
        value: DocumentSignerAttrType.ResponsiveItemValue,
        fieldIndex: DocumentSignerAttrType.ResponsiveItemFieldIndex,
        fieldLabel: DocumentSignerAttrType.ResponsiveItemFieldLabel,
        fieldRequired: DocumentSignerAttrType.ResponsiveItemFieldRequired,
        fieldAltText: DocumentSignerAttrType.ResponsiveItemFieldAltText,
      };
    case DocumentSignerAttrType.AgentResponsiveItemFieldMark:
      return {
        index: DocumentSignerAttrType.AgentResponsiveItemIndex,
        type: DocumentSignerAttrType.AgentResponsiveItemType,
        missing: DocumentSignerAttrType.AgentResponsiveItemMissing,
        wrong: DocumentSignerAttrType.AgentResponsiveItemWrong,
        requiredCount: DocumentSignerAttrType.AgentResponsiveItemRequiredCount,
        value: DocumentSignerAttrType.AgentResponsiveItemValue,
        fieldIndex: DocumentSignerAttrType.AgentResponsiveItemFieldIndex,
        fieldLabel: DocumentSignerAttrType.AgentResponsiveItemFieldLabel,
        fieldRequired: DocumentSignerAttrType.AgentResponsiveItemFieldRequired,
        fieldAltText: DocumentSignerAttrType.AgentResponsiveItemFieldAltText,
      };
    default:
      return {};
  }
};

const StyledCheckbox = styled("div")<
  EConsentMarkerProps & {
    isFulfilled: boolean;
    isChecked: boolean;
    altText?: string;
    isHidden: boolean;
    type?: DocumentSignerResponsiveItemTypeValue;
  }
>((props) => ({
  userSelect: "none",
  boxSizing: "border-box",
  display: props.isHidden ? "none" : "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "24px",
  height: "24px",
  position: "absolute",
  top: props.top,
  left: props.left,
  transform: "translateY(-50%)",
  borderRadius: "2px",
  border: `2px solid ${
    props.isModeSigning ? palette[defaultMarkerColor.additional.border] : palette[getColorBySignerIndex(props.marker.signerIndex).border]
  }`,

  outline: "none",
  backgroundColor: props.isModeSigning
    ? defaultMarkerColor.additional.background
    : getColorBySignerIndex(props.marker.signerIndex).background,

  color: "#878d96",
  fontSize: "16px",
  fontWeight: "700",
  whiteSpace: "nowrap",
  textIndent: 0,

  cursor: "pointer",

  "&:hover": {
    backgroundColor: props.isModeSigning
      ? defaultMarkerColor.additional.backgroundHover
      : getColorBySignerIndex(props.marker.signerIndex).backgroundHover,

    //NOTE: marker hover 시, UnlinkButton이 보임
    "& > span": {
      opacity: 1,
      width: "16px",
      height: "16px",
    },
  },

  "&":
    props.isModeSigning && (!props.isChecked || !props.isFulfilled)
      ? {
          animation: `${fadeInBorderMarker} 1.6s ease-in-out infinite`,
        }
      : {},

  "&:after": !props.isModeSigning
    ? {
        position: "relative",
        content: '""',
        borderRadius: props.type === DocumentSignerResponsiveItemTypeValue.Single ? "50%" : "2px",
        boxSizing: "border-box",
        border: `2px solid ${palette[getColorBySignerIndex(props.marker.signerIndex).text]}`,
        width: "18px",
        height: "18px",
      }
    : {},
}));
